// release-deploy-outcome-assessment.ts
//
// Luke 2026-09-30: "Với deploy, thêm cơ chế dừng chờ người khi kết quả sau
// crash hoặc receipt hỏng không xác định; không tự deploy lại."
//
// The deploy receipt (release-deploy-completion-receipt.ts) is written only
// after the recipe exits and its result is journaled, so a `run` that dies in
// between leaves no receipt — and the next run used to deploy again. Every
// deploy step journals an intent first (deployment-adapter.ts), so before any
// deploy decision this module asks what the journal knows about each deploy
// intent of the mission, through decideCrashRecoveryAction — the one place
// the "probe reality before any retry" rule lives (test 43/44):
//   - no result, `timed_out`, or `killed` by a signal ⇒ needs_human: the
//     recipe may have applied part or all of the deploy (a recipe that never
//     started is journaled `failed` — deployment-adapter.ts);
//   - `succeeded`, bound to THIS candidate + deploy hash ⇒ complete_receipt:
//     the deploy happened — write the missing receipt, never re-run it;
//   - `succeeded` with no candidate binding (CLI ≤ 3.33.0-beta.5) ⇒
//     needs_human: which candidate it deployed cannot be told;
//   - `succeeded` for another candidate / deploy step, or `failed` ⇒ not this
//     release's problem (a recipe that exited non-zero reported a failure;
//     the next run retrying it is unchanged behaviour).
// A valid deploy receipt for this release settles it before any of this; a
// damaged one is unknown as well. needs_human wins over every
// other answer: the mission stops until a person checks the target and
// either archives it or records the answer with `resolve-deploy` (close-out
// A5), which writes a manual `operation_result` this module then reads like
// any other (release-deploy-outcome-manual-resolution.ts).
import { decideCrashRecoveryAction } from "./deploy-crash-recovery.js";
import type { MissionState, OperationIntentRecord } from "./mission-state-types.js";
import type { ReleaseReceiptState } from "./release-deploy-completion-receipt.js";

export type DeployOutcomeAssessment =
  | { kind: "clear" }
  | { kind: "unknown"; reason: string }
  | {
      kind: "succeeded_without_receipt";
      operationId: string;
      recipeId: string;
      /** The `succeeded` result is a person's `resolve-deploy deployed` answer (review P2 M4) — the receipt must say so. */
      manualResolution?: { actor: string; at: string };
    };

/**
 * The operator-facing sentence for every "deploy outcome unknown" stop. Each
 * command is printed WHOLE (review P2 H1): a combined `deployed|not-deployed`
 * pasted into a shell is a pipe that records `deployed` nobody chose.
 */
export function deployOutcomeUnknownReason(cause: string, candidateSha: string): string {
  const resolve = `avatar autopilot resolve-deploy --candidate ${candidateSha} --outcome`;
  return (
    `Kết quả deploy không xác định: ${cause}. Autopilot KHÔNG deploy lại. ` +
    `Kiểm tay môi trường đích (bản đang chạy có phải candidate ${candidateSha.slice(0, 12)}?) ` +
    `rồi chạy ĐÚNG MỘT lệnh: đã deploy ⇒ \`${resolve} deployed\` (chỉ chạy smoke); ` +
    `chưa deploy ⇒ \`${resolve} not-deployed\` (cho deploy lại). Đừng đổi config.release trước khi ` +
    "trả lời. Bỏ mission ⇒ `avatar autopilot archive`."
  );
}

/** Review P2 M1: a damaged receipt is not answered by resolve-deploy — the file itself is the problem. */
function damagedReceiptReason(candidateSha: string): string {
  return (
    `Kết quả deploy không xác định: receipt release-state/deploy-${candidateSha}.json (trong runtime ` +
    "của mission) hỏng hoặc không đọc được. Autopilot KHÔNG deploy lại. Kiểm tay môi trường đích, " +
    "xoá (hoặc dời) đúng file đó rồi `avatar autopilot run` lại; bỏ mission ⇒ `avatar autopilot archive`."
  );
}

/**
 * Review P2 M3 — the ONE definition of "a person must answer this deploy
 * intent", shared with resolve-deploy: anything but a `failed` result or a
 * `succeeded` bound to a candidate (a missing result, timed_out, killed, an
 * unbound legacy `succeeded`, an unreadable outcome).
 */
export function deployIntentOutcomeIsUnknown(
  intent: OperationIntentRecord,
  outcome: string | undefined,
): boolean {
  if (outcome === "failed") return false;
  return !(outcome === "succeeded" && bindingOf(intent) !== undefined);
}

interface Binding {
  candidateSha: string;
  releaseDeployHash: string;
}

function bindingOf(intent: OperationIntentRecord): Binding | undefined {
  const { candidate_sha: candidateSha, release_deploy_hash: releaseDeployHash } = intent.params;
  return typeof candidateSha === "string" && typeof releaseDeployHash === "string"
    ? { candidateSha, releaseDeployHash }
    : undefined;
}

/** What the journal proves about one deploy intent; `undefined` = cannot tell. */
function journalProbe(
  outcome: string | undefined,
  binding: Binding | undefined,
  release: Binding,
): { alreadyApplied: boolean } | undefined {
  if (outcome === "failed") return { alreadyApplied: false };
  if (outcome !== "succeeded" || binding === undefined) return undefined;
  const thisRelease =
    binding.candidateSha === release.candidateSha &&
    binding.releaseDeployHash === release.releaseDeployHash;
  return { alreadyApplied: thisRelease };
}

function causeFor(intent: OperationIntentRecord, outcome: string | undefined): string {
  const binding = bindingOf(intent);
  const which = `deploy (operation ${intent.operation_id}${
    binding === undefined ? "" : `, candidate ${binding.candidateSha.slice(0, 12)}`
  })`;
  if (outcome === undefined)
    return `${which} đã bắt đầu nhưng journal không có kết quả — process chết giữa lúc deploy`;
  if (outcome === "timed_out") return `${which} bị dừng vì quá thời gian`;
  if (outcome === "killed") return `${which} bị signal giết giữa chừng`;
  if (outcome === "succeeded")
    return `${which} do CLI cũ ghi, không gắn candidate — không biết đã deploy bản nào`;
  return `${which} có kết quả "${outcome}" không rõ nghĩa`;
}

export async function assessDeployOutcome(input: {
  state: Pick<MissionState, "intents" | "results">;
  receipts: ReleaseReceiptState;
  candidateSha: string;
  releaseDeployHash: string;
}): Promise<DeployOutcomeAssessment> {
  if (input.receipts.deployReceiptDamaged) {
    return { kind: "unknown", reason: damagedReceiptReason(input.candidateSha) };
  }
  // A valid receipt already settles THIS release's deploy; older intents
  // (e.g. an unbound one from CLI ≤ beta.5) cannot unsettle it (review M2).
  if (input.receipts.deployed) return { kind: "clear" };
  const release = { candidateSha: input.candidateSha, releaseDeployHash: input.releaseDeployHash };
  const deployIntents = Object.values(input.state.intents)
    .filter((intent) => intent.operation_kind === "deploy")
    .sort((a, b) => a.seq - b.seq);
  let completed:
    | { operationId: string; recipeId: string; manualResolution?: { actor: string; at: string } }
    | undefined;
  for (const intent of deployIntents) {
    const outcome = input.state.results[intent.operation_id]?.outcome;
    const decision = await decideCrashRecoveryAction({
      operationKind: "deploy",
      nonIdempotent: intent.params.non_idempotent === true,
      probeRealState: async () => journalProbe(outcome, bindingOf(intent), release),
    });
    if (decision.action === "needs_human") {
      return {
        kind: "unknown",
        // M1: name the candidate the deploy was for — resolve-deploy matches on it.
        reason: deployOutcomeUnknownReason(
          causeFor(intent, outcome),
          bindingOf(intent)?.candidateSha ?? input.candidateSha,
        ),
      };
    }
    if (decision.action === "complete_receipt") {
      const recipeId = intent.params.recipe_id;
      const manual = input.state.results[intent.operation_id]?.manual_resolution;
      completed = {
        operationId: intent.operation_id,
        recipeId: typeof recipeId === "string" ? recipeId : "unknown",
        ...(manual?.answer === "deployed"
          ? { manualResolution: { actor: manual.actor, at: manual.at } }
          : {}),
      };
    }
  }
  return completed === undefined
    ? { kind: "clear" }
    : { kind: "succeeded_without_receipt", ...completed };
}
